/**
 * Loads a projection and keeps the previous value while refreshing, so a live
 * update never blanks the screen an operator is reading.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiRequestError } from '../lib/api';

export interface ProjectionState<T> {
  data: T | null;
  error: ApiRequestError | Error | null;
  loading: boolean;
  refreshing: boolean;
  /** True when the last refresh failed but earlier data is still displayed. */
  degraded: boolean;
  loadedAt: string | null;
  reload: () => void;
}

export function useProjection<T>(load: () => Promise<T>, deps: unknown[] = []): ProjectionState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiRequestError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const loader = useRef(load);
  loader.current = load;
  const mounted = useRef(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hadData = data !== null;
    if (hadData) setRefreshing(true);
    else setLoading(true);

    loader
      .current()
      .then((result) => {
        if (cancelled || !mounted.current) return;
        setData(result);
        setError(null);
        setDegraded(false);
        setLoadedAt(new Date().toISOString());
      })
      .catch((err: Error) => {
        if (cancelled || !mounted.current) return;
        // Keep the last known data on screen and mark it degraded rather than
        // replacing useful context with an error page.
        if (hadData) setDegraded(true);
        else setError(err);
      })
      .finally(() => {
        if (cancelled || !mounted.current) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, refreshing, degraded, loadedAt, reload };
}
